import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import uniqid from 'uniqid'

import PerfectScrollbar from 'react-perfect-scrollbar'

import { TruncateAndTooltip } from '../truncateAndTooltip'

import { useViewport } from '../../hooks'

import {
  DataTableActionMenu,
  DataTableActionMenuTrigger,
  DataTableContainer
} from './DataTable.styles'

import './DataTable.scss'

export const PARAM_ORDER_ASC = 'ASC'
export const PARAM_ORDER_DESC = 'DESC'

const DataTableRowPlaceholder = React.memo(({ rows, stencils }) =>
  [...Array(rows)].map((c, rowIndex) => (
    <tr
      className='c-datatable__row c-datatable__row--fetching'
      key={`row-${rowIndex}`}
    >
      {stencils &&
        stencils.map((stencil, cellIndex) => (
          <td
            key={`row-${rowIndex}-cell-${cellIndex}`}
            className='c-datatable__cell'
          >
            {stencil || <span />}
          </td>
        ))}
    </tr>
  ))
)

DataTableRowPlaceholder.propTypes = {
  rows: PropTypes.number,
  stencils: PropTypes.array
}

export const DataTable = ({
  className,
  head,
  body,
  onClickSort,
  loading,
  rowPlaceholder,
  rowPlaceholderStencils,
  rows,
  sortable,
  sortBy,
  sortOrder,
  'data-mu-test': dataAttributeTest,
  //
  stickyHead,
  hScroll,
  actions
}) => {
  const { current: uuid } = useRef(uniqid('table'))

  const containerRef = useRef(null)
  const spyTopRef = useRef(null)

  const stickyContainerRef = useRef(null)
  const tableRef = useRef(null)
  const tableStickyRef = useRef(null)

  const scrollRef = useRef(null)
  const scrollContainerRef = useRef(null)

  const headScrollRef = useRef(null)
  const headScrollContainerRef = useRef(null)

  const intersectionTopObserver = useRef(null)

  const intersectionTableObserver = useRef(null)

  const intersectionsRef = useRef({
    top: undefined,
    table: undefined,
    delta: undefined
  })

  const [overlayLeftPos, setOverlayLeftPos] = useState(0)
  const overlayLeftRef = useRef(null)

  const [overlayRightPos, setOverlayRightPos] = useState(0)
  const overlayRightRef = useRef(null)

  const lastScrollLeft = useRef(0)

  const actionMenuRef = useRef(null)

  const handleResize = aaa => {
    if (stickyContainerRef.current && containerRef.current) {
      stickyContainerRef.current.style.width = `${containerRef.current.clientWidth}px`
      stickyContainerRef.current.style.maxWidth = `${containerRef.current.clientWidth}px`
    }

    headScrollRef.current && headScrollRef.current._ps.update()
    scrollRef.current && scrollRef.current._ps.update()
  }

  useViewport({ resize: handleResize })

  const handleSticky = ({ top, table, delta }) => {
    if (stickyHead && stickyContainerRef.current) {
      if (!top && !!table) {
        stickyContainerRef.current.classList.add(
          'c-datatable-sticky-container--sticky'
        )
      } else {
        stickyContainerRef.current.classList.remove(
          'c-datatable-sticky-container--sticky'
        )
      }

      if (delta < 60) {
        stickyContainerRef.current.classList.add(
          'c-datatable-sticky-container--hidden'
        )
      } else {
        stickyContainerRef.current.classList.remove(
          'c-datatable-sticky-container--hidden'
        )
      }
    }
  }

  const { current: intersectionTopCallback } = useRef(entries => {
    entries.forEach(entry => {
      intersectionsRef.current = {
        ...intersectionsRef.current,
        top: entry.isIntersecting
      }

      handleSticky(intersectionsRef.current)
    })
  })

  const { current: intersectionTableCallback } = useRef(entries => {
    entries.forEach(entry => {
      intersectionsRef.current = {
        ...intersectionsRef.current,
        table: entry.isIntersecting,
        delta: entry.boundingClientRect.height + entry.boundingClientRect.top
      }

      handleSticky(intersectionsRef.current)
    })
  })

  const spyTopCallback = useCallback(node => {
    if (node) {
      spyTopRef.current = node
      if (intersectionTopObserver.current) {
        intersectionTopObserver.current.disconnect()
      }

      intersectionTopObserver.current = new window.IntersectionObserver(
        intersectionTopCallback,
        {
          root: null,
          rootMargin: '0px',
          threshold: 1.0
        }
      )
      intersectionTopObserver.current.observe(node)
    }
    spyTopRef.current = undefined
  })

  const tableCallback = useCallback(node => {
    if (node) {
      tableRef.current = node
      if (intersectionTableObserver.current) {
        intersectionTableObserver.current.disconnect()
      }

      intersectionTableObserver.current = new window.IntersectionObserver(
        intersectionTableCallback,
        {
          root: null,
          rootMargin: '0px',
          threshold: [
            ...Array.from({ length: 100 }).map((o, i) => i / 100),
            1.0
          ]
        }
      )

      intersectionTableObserver.current.observe(node)

      //  same width for table head and table body
      node.querySelectorAll('tr:nth-of-type(1) td').forEach((cell, index) => {
        const headCell = tableStickyRef.current.querySelector(
          `th:nth-of-type(${cell.cellIndex + 1})`
        )

        const w = Math.max(cell.clientWidth, headCell.clientWidth)

        headCell.style.width = `${w}px`
        headCell.style.minWidth = `${w}px`
        headCell.style.maxWidth = `${w}px`

        cell.style.width = `${w}px`
        cell.style.minWidth = `${w}px`
        cell.style.maxWidth = `${w}px`
      })

      const stickyLeftPos = Array.from(
        tableStickyRef.current.querySelectorAll('th[data-sticky-left]')
      ).reduce(
        (acc, curr) => {
          return [...acc, acc.reduce((a, c) => a + c, curr.clientWidth)]
        },
        [0]
      )

      const stickyRightPos = Array.from(
        tableStickyRef.current.querySelectorAll('th[data-sticky-right]')
      )
        .reverse()
        .reduce(
          (acc, curr) => {
            return [...acc, acc.reduce((a, c) => a + c, curr.clientWidth)]
          },
          [0]
        )

      node.querySelectorAll('td[data-sticky-left]').forEach(el => {
        el.style.left = `${stickyLeftPos[el.cellIndex]}px`
      })

      tableStickyRef.current
        .querySelectorAll('th[data-sticky-left]')
        .forEach(el => {
          el.style.left = `${stickyLeftPos[el.cellIndex]}px`
        })

      if (hScroll) {
        if (stickyLeftPos.length > 0) {
          setOverlayLeftPos(stickyLeftPos[stickyLeftPos.length - 1])
        }

        if (stickyRightPos.length > 0) {
          setOverlayRightPos(stickyRightPos[stickyRightPos.length - 1])
        }
      }

      headScrollRef.current && headScrollRef.current._ps.update()
    }

    tableRef.current = undefined
  })

  useLayoutEffect(() => {
    handleResize()
  }, [head, body, loading, rows, actions])

  useEffect(() => {
    handleScrollX()

    setTimeout(() => {
      headScrollRef.current && headScrollRef.current._ps.update()
      scrollRef.current && scrollRef.current._ps.update()
    }, 100)
  }, [])

  useEffect(() => {
    if (hScroll) {
      headScrollContainerRef.current.scrollLeft = lastScrollLeft.current
      scrollContainerRef.current.scrollLeft = lastScrollLeft.current
    }
  })

  const handleScrollX = () => {
    if (hScroll) {
      if (
        !headScrollContainerRef.current.querySelector(
          '.ps__rail-x.ps--clicking'
        )
      ) {
        headScrollContainerRef.current.scrollLeft =
          scrollContainerRef.current.scrollLeft

        lastScrollLeft.current = scrollContainerRef.current.scrollLeft

        if (scrollContainerRef.current.scrollLeft > 0) {
          overlayLeftRef.current.classList.add(
            'c-datatable__overlay-left--visible'
          )
        }

        overlayRightRef.current.classList.add(
          'c-datatable__overlay-right--visible'
        )
      }
    }
  }

  const handleScrollHeadX = () => {
    if (hScroll) {
      if (
        !scrollContainerRef.current.querySelector('.ps__rail-x.ps--clicking')
      ) {
        scrollContainerRef.current.scrollLeft =
          headScrollContainerRef.current.scrollLeft

        lastScrollLeft.current = headScrollContainerRef.current.scrollLeft

        if (headScrollContainerRef.current.scrollLeft > 0) {
          overlayLeftRef.current.classList.add(
            'c-datatable__overlay-left--visible'
          )
        }

        overlayRightRef.current.classList.add(
          'c-datatable__overlay-right--visible'
        )
      }
    }
  }

  const handleReachXStart = () => {
    overlayLeftRef.current &&
      overlayLeftRef.current.classList.remove(
        'c-datatable__overlay-left--visible'
      )
  }

  const handleReachXEnd = () => {
    overlayRightRef.current &&
      overlayRightRef.current.classList.remove(
        'c-datatable__overlay-right--visible'
      )
  }

  //
  const handleCellWidth = width =>
    width
      ? {
          width: `${width}px `,
          minWidth: `${width}px `,
          maxWidth: `${width}px `
        }
      : undefined
  //

  const placeholder = rowPlaceholder || (
    <DataTableRowPlaceholder
      rows={rows}
      stencils={rowPlaceholderStencils || (!!head && head.map(() => true))}
    />
  )

  const renderContent = (content, truncate = false) =>
    truncate && typeof content === 'string' ? (
      <TruncateAndTooltip text={content} forceTooltipRebuild={false} />
    ) : (
      content
    )

  const headContent = Array.isArray(head) && (
    <tr className='c-datatable__row'>
      {head.map(
        (
          {
            span,
            content,
            sortId,
            align,
            stickyLeft,
            stickyRight,
            width,
            truncate
          },
          i
        ) => (
          <th
            key={`${uuid}-head-${i}`}
            className={classnames(
              'c-datatable__cell',

              span && `c-datatable__cell--${span}`,
              {
                'c-datatable__cell--align-right': align === 'right',
                'c-datatable__cell--sticky-left': stickyLeft,
                'c-datatable__cell--sticky-right': stickyRight
              }
            )}
            style={handleCellWidth(width)}
            data-sticky-left={stickyLeft ? true : undefined}
            data-sticky-right={stickyRight ? true : undefined}
          >
            <div className='c-datatable__cell-title-wrapper'>
              <a
                onClick={
                  !sortable || loading ? undefined : () => onClickSort(sortId)
                }
                className={classnames('c-datatable__cell-title', {
                  'c-datatable__cell-title--clickable': sortable && sortId
                })}
              >
                {renderContent(content, truncate)}
              </a>
              {sortable && sortId && !loading && (
                <a
                  onClick={
                    !sortable || loading ? undefined : () => onClickSort(sortId)
                  }
                  className={classnames('c-datatable__cell-title', {
                    'c-datatable__cell-title--sortable': sortable && sortId,
                    'c-datatable__cell-title--sortable--active':
                      sortable && sortId && sortBy === sortId,
                    'c-datatable__cell-title--sortable--asc':
                      sortable &&
                      sortId &&
                      sortBy === sortId &&
                      sortOrder === PARAM_ORDER_ASC,
                    'c-datatable__cell-title--sortable--desc':
                      sortable &&
                      sortId &&
                      sortBy === sortId &&
                      sortOrder === PARAM_ORDER_DESC
                  })}
                />
              )}
            </div>
          </th>
        )
      )}
      {actions.length > 0 && (
        <th
          className='c-datatable__cell c-datatable__cell--align-right c-datatable__cell--sticky-right'
          data-sticky-right
        >
          {' '}
        </th>
      )}
    </tr>
  )

  const handleActionMenuTriggerClick = (
    rowIndex,
    { top, bottom, targetRect }
  ) => {
    actionMenuRef.current.showMenu(rowIndex, { top, bottom, targetRect })
  }

  const bodyRows = body.map((row, iRow) => (
    <tr
      className='c-datatable__row'
      key={`${uuid}-body-row-${iRow}`}
      data-mu-test='results-table__row'
    >
      {row.map(({ content, highlight, align }, iCell) => (
        <td
          key={`${uuid}-body-row-${iRow}-cell-${iCell} `}
          className={classnames(
            'c-datatable__cell',
            head[iCell].span && `c-datatable__cell--${head[iCell].span}`,
            {
              'c-datatable__cell--highlight': highlight,
              'c-datatable__cell--sticky-left': head[iCell].stickyLeft,
              'c-datatable__cell--sticky-right': head[iCell].stickyRight,

              'c-datatable__cell--align-right':
                align === 'right' || (!!head && head[iCell].align === 'right'),
              'c-datatable__cell--sortable':
                !!head && head[iCell].sortable && head[iCell].sortId
            }
          )}
          style={handleCellWidth(head[iCell].width)}
          data-sticky-left={head[iCell].stickyLeft ? true : undefined}
        >
          {renderContent(content, head[iCell].truncate)}
        </td>
      ))}
      {actions[iRow] && (
        <td
          className='c-datatable__cell c-datatable__cell--align-right c-datatable__cell--sticky-right'
          data-sticky-right
        >
          <DataTableActionMenuTrigger
            disabled={actions[iRow].disabled}
            rowIndex={iRow}
            onClick={handleActionMenuTriggerClick}
          />
        </td>
      )}
    </tr>
  ))

  const HeaderWrapper = hScroll
    ? ({ children }) => (
        <>
          <PerfectScrollbar
            className='c-datatable-ps'
            options={{ suppressScrollY: true }}
            containerRef={ref => (headScrollContainerRef.current = ref)}
            ref={headScrollRef}
            onScrollX={handleScrollHeadX}
            onXReachStart={handleReachXStart}
            onXReachEnd={handleReachXEnd}
          >
            {children}
          </PerfectScrollbar>
        </>
      )
    : ({ children }) => children

  const Wrapper = hScroll
    ? ({ children }) => (
        <>
          <div className='c-datatable__overlay-container'>
            <PerfectScrollbar
              options={{ useBothWheelAxes: true, suppressScrollY: true }}
              className='c-datatable-ps'
              onScrollX={handleScrollX}
              onXReachStart={handleReachXStart}
              onXReachEnd={handleReachXEnd}
              containerRef={ref => (scrollContainerRef.current = ref)}
              ref={scrollRef}
            >
              {children}
            </PerfectScrollbar>

            <DataTableActionMenu ref={actionMenuRef} actions={actions} />
          </div>
        </>
      )
    : ({ children }) => children

  return (
    <DataTableContainer ref={containerRef} $hScroll={hScroll}>
      <div
        ref={stickyContainerRef}
        className={classnames('c-datatable-sticky-container')}
      >
        <HeaderWrapper>
          <table
            ref={tableStickyRef}
            className={classnames('c-datatable', 'c-datatable--head', {
              'c-datatable--hscroll': hScroll
            })}
          >
            <thead>{headContent}</thead>
          </table>
        </HeaderWrapper>
      </div>

      <div className='c-datatable__spy-top-ref' ref={spyTopCallback} />

      <Wrapper>
        <table
          ref={tableCallback}
          className={classnames(
            'c-datatable',
            'c-datatable--body',
            {
              'c-datatable--hscroll': hScroll
            },
            className
          )}
          data-mu-test={classnames('data-table', dataAttributeTest)}
        >
          <tbody>{loading ? placeholder : bodyRows}</tbody>
        </table>
      </Wrapper>

      {hScroll && (
        <>
          <div
            className='c-datatable__overlay-left'
            ref={overlayLeftRef}
            style={{
              left: `${overlayLeftPos}px`
            }}
          />
          <div
            className='c-datatable__overlay-right c-datatable__overlay-right--visible'
            ref={overlayRightRef}
            style={{
              right: `${overlayRightPos}px`
            }}
          />
        </>
      )}
    </DataTableContainer>
  )
}

DataTable.defaultProps = {
  rows: 10,
  body: [],
  //
  stickyHead: false,
  hScroll: false,
  actions: []
}

/* istanbul ignore next */
DataTable.propTypes = {
  /**
   * An array of arrays representing cell inside each row. Each cell is
   * represented by an object containing a `content` and a `highlight` key
   **/
  body: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        content: PropTypes.any,
        highlight: PropTypes.bool,
        align: PropTypes.string
      })
    )
  ),
  /**
   * Additional classnames
   */
  className: PropTypes.string,
  /**
   * An array representing table head cells. Each cell is represented
   * by an object with the following keys<br />
   * - span (number) the width of the column expressed in twelves (optional)<br />
   * - content (any) the content of the cell itself<br />
   * - sortId (string) the id passed as argument to onClickSort<br />
   * - align (string) `left | right` the text alignment for the column
   * - stickyLeft (boolean)
   * - stickyRight (boolean)
   * - width (number) the width of the column expressed in pixel (required for truncate effect)<br />
   * - truncate (boolean) wrap with truncate and tooltip (only for string content)
   */
  head: PropTypes.array.isRequired,
  /**
   * Flag to display loading status
   */
  loading: PropTypes.bool,
  /**
   * Additional classnames
   */
  onClickSort: PropTypes.func,
  rows: PropTypes.number,
  rowPlaceholder: PropTypes.oneOfType([
    PropTypes.instanceOf(React.Component, PropTypes.func)
  ]),
  rowPlaceholderStencils: PropTypes.array,
  /**
   * Whether or not the table is sortable
   */
  sortable: PropTypes.bool,
  /**
   * Initial sortBy key. Must be one of the values specified in `head[item].sortId`
   */
  sortBy: (props, propName, componentName) => {
    if (!props[propName]) return
    const isValid = props.head
      .map(({ sortId }) => sortId)
      .filter(c => !!c)
      .includes(props[propName])
    if (!isValid) {
      return new Error(
        `Invalid prop \`${propName}\` supplied to
        \`${componentName}\`. Value must be one of \`sortId\`s
        specified in \`props.head\` array`
      )
    }
  },
  /**
   *  Sort order
   **/
  sortOrder: PropTypes.oneOf([PARAM_ORDER_ASC, PARAM_ORDER_DESC]),
  /**
   * Additional values for data-attribute used for tests and tracking
   **/
  'data-mu-test': PropTypes.string,
  /***************************************************************************/
  stickyHead: PropTypes.bool,
  hScroll: PropTypes.bool,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      items: PropTypes.array,
      className: PropTypes.string,
      disabled: PropTypes.bool,
      'data-mu-test': PropTypes.string
    })
  )
}

/*
      if (head[col].stickyRight) {
        if (col === head.length - 1) {
          el.style.right = 0
        } else {
          let right = 0

          el.parentNode
            .querySelectorAll('.c-datatable__cell--sticky-right')

            .forEach((cellElement, idx) => {
              if (col < cellElement.cellIndex) {
                right += cellElement.clientWidth
              }
            })
          el.style.right = `${right}px`
        }
      }
      */

/*
if (head[col].stickyRight) {
  if (col === head.length - 1) {
    el.style.right = 0
  } else {
    let right = 0

    el.parentNode
      .querySelectorAll('.c-datatable__cell--sticky-right')

      .forEach((cellElement, idx) => {
        if (col < cellElement.cellIndex) {
          right += cellElement.clientWidth
        }
      })
    el.style.right = `${right}px`
  }
}
*/
